import type { Intersection, ValuesType } from 'utility-types'

import { type ExtendedMap, isArrayNotEmpty, objectEntries, type ReadonlyRecord } from '../../../../utils'

import type { Capability, CybusPersistedPermission } from '../../../../domain'

import type { AuthenticationResponse, SessionResponse } from '../../../../application'

import {
    type BackendDefinition,
    capabilities,
    type PermissionRequirement,
    unavailableByContainerImplementation,
    unavailableMfaConfiguration,
} from '../../../Connectware'
import type { MinimalLoginResponse, MinimalSessionResponse } from '../Types'
import { mapPersistedPermissions } from './Permission'

class Alternative {
    private static readonly permissionPatternCache = new Map<string, RegExp>()

    /**
     * Yields whether a given permission access covers the given requirment
     *
     * @example /api/v2/resources/#, /api/v2/resources/states/deviations/count -> true
     * @example /api/v2/services/#, /api/v2/resources/states/deviations/count -> false
     * @example /api/v2/services/+, /api/v2/services/+ -> true
     */
    private static doesResourceMatchRequirement (
        resource: CybusPersistedPermission['resource'],
        requirement: NonNullable<PermissionRequirement['resource']>
    ): boolean {
        let resourcePattern = this.permissionPatternCache.get(resource)

        if (!resourcePattern) {
            /**
             * + signs are replaced with a "grab everything before the next slash"
             * /# segments are replaced with a "grab everything and empty path afterward"
             */
            resourcePattern = new RegExp(`^${resource.replaceAll('+', '[^/]+').replaceAll('/#', '.*')}$`)
            this.permissionPatternCache.set(resource, resourcePattern)
        }

        return resourcePattern.test(requirement)
    }

    private static matches (permission: CybusPersistedPermission, requirement: PermissionRequirement): boolean {
        return (
            requirement.context === permission.context &&
            // Has read permission superior or equal to the required
            permission.read >= requirement.read &&
            // Has write permission superior or equal to the required
            permission.write >= requirement.write &&
            (requirement.resource === null ||
                requirement.resource === permission.resource ||
                this.doesResourceMatchRequirement(permission.resource, requirement.resource))
        )
    }

    constructor (private requirements: readonly PermissionRequirement[]) {}

    collect (permission: CybusPersistedPermission): boolean {
        this.requirements = this.requirements.filter((requirement) => !Alternative.matches(permission, requirement))
        return !isArrayNotEmpty(this.requirements)
    }
}

class PermissionCollector {
    private readonly alternatives: readonly Alternative[]

    constructor (readonly capability: Capability, requirements: readonly (readonly PermissionRequirement[])[]) {
        this.alternatives = requirements.map((alternative) => new Alternative(alternative))
    }

    /**
     * @returns if it's done collecting
     */
    collect (permission: CybusPersistedPermission): boolean {
        return this.alternatives.some((a) => a.collect(permission))
    }
}

const mapPermissionsRequirements = (inputPermissions: CybusPersistedPermission[]): Capability[] => {
    const foundCapabilities: Capability[] = []

    let permissionsCollectors = objectEntries(capabilities).map(([permission, requirements]) => new PermissionCollector(permission, requirements))

    inputPermissions.every((permission) => {
        permissionsCollectors = permissionsCollectors.filter((collector) => {
            if (!collector.collect(permission)) {
                /**
                 * Not done collecting
                 */
                return true
            }

            /**
             * Collector has matched all rules
             * So add them and remove this from the next iteration
             */
            foundCapabilities.push(collector.capability)

            return false
        })

        return Boolean(permissionsCollectors.length)
    })

    return Array.from(new Set(foundCapabilities))
}

export type CapabilityCheckers = Readonly<{
    orchestrator: () => Promise<BackendDefinition<'container-manager', 'Orchestrator'> | null>
    isMfaActive: () => Promise<boolean>
}>

/** This type forces the correct assigning of checkers */
type CheckersConstraints<T extends ReadonlyRecord<string, ValuesType<CapabilityCheckers>>> = {
    [P in keyof T]: ExtendedMap<Awaited<ReturnType<T[P]>>, Capability[]>
}

const constraints: CheckersConstraints<CapabilityCheckers> = { orchestrator: unavailableByContainerImplementation, isMfaActive: unavailableMfaConfiguration }

const hasCapabilitiesToCheck = (indexedCapabilities: Set<Capability>, mayBeRemovedMap: Map<unknown, Capability[]>): boolean => {
    for (const capabilities of mayBeRemovedMap.values()) {
        if (capabilities.some((capability) => indexedCapabilities.has(capability))) {
            /** There are capabilities present that require to check if backend can perform them  */
            return true
        }
    }

    return false
}

const resolveCapabilities = (capabilities: Capability[], checkers: CapabilityCheckers): Promise<Capability[]> => {
    /** Can be used for multiple checks without performance degradation */
    const indexedCapabilities = new Set(capabilities)

    return (
        Promise.all(
            objectEntries(constraints).map(([promiseGetterName, mayBeRemovedMap]) => {
                if (!hasCapabilitiesToCheck(indexedCapabilities, mayBeRemovedMap)) {
                    /** There is nothing to remove, so don't perform checks */
                    return Promise.resolve()
                }

                /**
                 * With the key it is possible to retrieve
                 * which capabilities need to be dropped
                 * as the backend cannot support them
                 */
                const keyPromise = checkers[promiseGetterName]()

                return keyPromise.then((key) => {
                    const castMap: ExtendedMap<typeof key, Capability[]> = mayBeRemovedMap

                    /** Finally drop them */
                    castMap.getDefault(key, []).forEach((c) => indexedCapabilities.delete(c))
                })
            })
        )
            /** Wait for everyone to resolve, then return capabilities that are left */
            .then(() => Array.from(indexedCapabilities))
    )
}

type LoginResponseWithToken = Extract<MinimalLoginResponse, Readonly<{ token: string }>>

const mapAuthentication = (
    { permissions, expiresAt }: Intersection<LoginResponseWithToken, MinimalSessionResponse>,
    checkers: CapabilityCheckers
): Promise<Intersection<AuthenticationResponse, SessionResponse>> => {
    const capabilities = mapPermissionsRequirements(mapPersistedPermissions(permissions))
    return resolveCapabilities(capabilities, checkers).then((capabilities) => ({ capabilities, expiresAt: new Date(expiresAt) }))
}

export const mapLoginResponse = ({ token, ...args }: LoginResponseWithToken, checkers: CapabilityCheckers): Promise<AuthenticationResponse> =>
    mapAuthentication(args, checkers).then((response) => ({ ...response, token }))

export const mapSessionResponse = ({ username, ...args }: MinimalSessionResponse, checkers: CapabilityCheckers): Promise<SessionResponse> =>
    mapAuthentication(args, checkers).then((response) => ({ ...response, username }))
